import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TrendingUp, ImageIcon, Newspaper } from 'lucide-react'
import { newsApi } from '@/services/api'
import { getGradientForId } from '@/lib/config'
import { Skeleton } from '@/components/ui/skeleton'

function TopStoryItem({ article, index }) {
  const { slug, alias, title, feeder, thumbnail, resized_featured_image } = article
  // Image priority: thumbnail > resized_featured_image > gradient
  const imageUrl = thumbnail || resized_featured_image
  const hasImage = !!imageUrl
  const gradientStyle = !hasImage ? { background: getGradientForId(alias || slug) } : {}

  return (
    <Link
      to={`/posts/${slug}`}
      className="group flex gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0"
    >
      {/* Rank badge */}
      <div className="flex-shrink-0">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
          index === 0 ? 'bg-yellow-100 text-yellow-700' :
          index === 1 ? 'bg-gray-100 text-gray-700' :
          index === 2 ? 'bg-orange-100 text-orange-700' :
          'bg-primary/10 text-primary'
        }`}>
          {index + 1}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary transition-colors mb-1">
          {title}
        </h3>
        {feeder && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1 truncate font-medium">
            <Newspaper className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{feeder.display_name || feeder.name}</span>
          </div>
        )}
      </div>

      {/* Thumbnail */}
      <div className="flex-shrink-0">
        {hasImage ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-16 h-16 rounded-lg object-cover"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-16 h-16 rounded-lg flex items-center justify-center"
            style={gradientStyle}
          >
            <ImageIcon className="w-6 h-6 text-white/80" />
          </div>
        )}
      </div>
    </Link>
  )
}

function TopStoryItemSkeleton() {
  return (
    <div className="flex gap-3 p-3">
      <Skeleton className="w-8 h-8 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
      <Skeleton className="w-16 h-16 rounded-lg" />
    </div>
  )
}

export function TopStoriesWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['topStories'],
    queryFn: () => newsApi.getTopStories(),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  })

  // Handle paginated response - get first 6 results
  const topStories = data?.data?.results?.slice(0, 6) || []

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div data-logo-box className="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3 transition-all duration-300">
        <div className="flex items-center gap-2 text-white">
          <h2 className="font-bold text-lg">Top Stories</h2>
        </div>
      </div>

      {/* Content */}
      <div className="divide-y divide-gray-100">
        {isLoading && (
          <>
            {[...Array(5)].map((_, i) => (
              <TopStoryItemSkeleton key={i} />
            ))}
          </>
        )}

        {isError && (
          <div className="p-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Failed to load top stories</p>
          </div>
        )}

        {!isLoading && !isError && topStories.length === 0 && (
          <div className="p-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">No top stories available</p>
          </div>
        )}

        {!isLoading && !isError && topStories.map((story, index) => (
          <TopStoryItem key={story.alias || story.id} article={story} index={index} />
        ))}
      </div>

    </div>
  )
}
